import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  useTheme,
  useMediaQuery,
  TablePagination,
  Skeleton,
  Switch,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import config from '../../Config';

export default function PropertyManagement() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusLoading, setStatusLoading] = useState({});

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${config.apiBaseUrl}/admin/getProperties`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();

        if (response.ok && result?.data) {
          setProperties(result.data);
        } else {
          setProperties([]);
        }
      } catch (error) {
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedProperties = properties.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handlePropertyClick = (id) => {
    navigate(`/propertydetail/${id}`);
  };

  const handleToggleStatus = async (propertyId, currentStatus) => {
    const newStatus = !currentStatus;

    const result = await Swal.fire({
      title: newStatus ? 'Activate this property?' : 'Deactivate this property?',
      text: newStatus
        ? 'Are you sure you want to activate this property?'
        : 'Are you sure you want to deactivate this property?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus ? '#3085d6' : '#d33',
      cancelButtonColor: '#999',
      confirmButtonText: newStatus ? 'Yes, activate it!' : 'Yes, deactivate it!',
    });

    if (!result.isConfirmed) return;

    setStatusLoading((prev) => ({ ...prev, [propertyId]: true }));

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${config.apiBaseUrl}/admin/updatePropertyStatus/${propertyId}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isActive: newStatus }),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setProperties((prev) =>
          prev.map((p) => (p.propertyID === propertyId ? { ...p, isActive: newStatus } : p))
        );

        Swal.fire({
          title: 'Success!',
          text: `Property has been ${newStatus ? 'activated' : 'deactivated'} successfully.`,
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to update property status.',
          icon: 'error',
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: 'Something went wrong while updating the status.',
        icon: 'error',
      });
    } finally {
      setStatusLoading((prev) => ({ ...prev, [propertyId]: false }));
    }
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Typography variant={isSmDown ? 'h5' : 'h4'} fontWeight="bold" mb={3}>
        Property Management
      </Typography>

      <Paper elevation={3} sx={{ p: isSmDown ? 2 : 3, borderRadius: 3 }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Property ID</strong>
                </TableCell>
                <TableCell>
                  <strong>Property Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Phone</strong>
                </TableCell>
                <TableCell>
                  <strong>Email</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Active Status</strong>
                </TableCell>
                <TableCell align="center">
                  <strong>Map Upsell</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading
                ? Array.from({ length: rowsPerPage }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Skeleton width={100} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={160} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={100} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={180} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={80} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={80} />
                      </TableCell>
                    </TableRow>
                  ))
                : paginatedProperties.map((property) => {
                    const canViewUpsell = property.isActive && property.hasUpSale;

                    return (
                      <TableRow key={property.propertyID} hover>
                        <TableCell
                          sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          onClick={() => handlePropertyClick(property.propertyID)}
                        >
                          {property.propertyID}
                        </TableCell>

                        <TableCell>{property.propertyName || '-'}</TableCell>
                        <TableCell>{property.propertyPhone || '-'}</TableCell>
                        <TableCell>{property.propertyEmail || '-'}</TableCell>

                        <TableCell align="center">
                          {statusLoading[property.propertyID] ? (
                            <CircularProgress size={22} />
                          ) : (
                            <Switch
                              checked={!!property.isActive}
                              color="success"
                              onChange={() =>
                                handleToggleStatus(property.propertyID, property.isActive)
                              }
                            />
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip
                            title={
                              !property.isActive
                                ? 'Please activate the property first'
                                : !property.hasUpSale
                                  ? 'Upsell is not enabled for this property'
                                  : 'View upsell details'
                            }
                          >
                            <Typography
                              sx={{
                                color: canViewUpsell ? 'primary.main' : 'text.disabled',
                                cursor: canViewUpsell ? 'pointer' : 'not-allowed',
                                '&:hover': canViewUpsell ? { textDecoration: 'underline' } : {},
                              }}
                              onClick={() => {
                                if (!property.isActive) {
                                  Swal.fire({
                                    title: 'Inactive Property',
                                    text: 'Please activate the property first.',
                                    icon: 'info',
                                    timer: 1500,
                                  });
                                  return;
                                }

                                if (!property.hasUpSale) {
                                  Swal.fire({
                                    title: 'Upsell Disabled',
                                    text: 'Upsell is not enabled for this property.',
                                    icon: 'info',
                                    timer: 1500,
                                  });
                                  return;
                                }

                                navigate(`/propertyUpsales/${property._id}`);
                              }}
                            >
                              View Upsell
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}

              {!loading && properties.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    No properties found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          component="div"
          count={properties.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Paper>
    </Box>
  );
}
